<template>
  <v-autocomplete
    data-testid="yard-view-spot-select"
    variant="outlined"
    color="primary"
    :items="spotsByArea"
    item-value="id"
    item-title="code"
    placeholder="Select a spot"
    :return-object="false"
    :model-value="value"
    @update:search="$e => (searchTerm = $e)"
    @update:model-value="
      $emit('input', $event);
      value = null;
    "
    density="compact"
    :clearable="false"
    hide-details>
    <template #no-data>
      <v-list-item :title="t('yard.messages.noSpotsAvailable')"></v-list-item>
    </template>
    <template #item="{ item, props }">
      <v-list-subheader v-if="item.raw.header" :title="item.raw.header"></v-list-subheader>

      <v-list-item v-else v-bind="props" :title="item.title">
        <template #prepend>
          <v-icon size="x-small">{{ item.raw.icon }}</v-icon>
        </template>

        <template #append v-if="searchTerm">
          <span class="font-weight-regular text-uppercase ml-2">({{ item.raw.group }})</span>
        </template>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<script>
import { str as StrLib } from '@nova/core';
import { computed, defineComponent, ref } from 'vue';
import useYard from '@/modules/yard/composables/useYard';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewSpotSelect',
  emits: ['input'],
  props: {
    showClosedSpots: {
      type: Boolean,
      default: false
    },
    assetType: {
      type: String,
      default: 'assetVisit',
      validator: value => ['assetVisit', 'assetContainer'].includes(value),
      required: false
    }
  },
  setup(props) {
    const yardStore = useYard();
    const { t } = useI18n();
    const searchTerm = ref('');
    const value = ref(null);

    const spotsByArea = computed(() => {
      const items = [];
      const allSpots = yardStore.spotsRef.value || [];
      const areas = yardStore.spotAreasRef.value || [];

      for (const area of areas) {
        const areaSpots = allSpots
          .filter(spot => spot.spotAreaId === area.id)
          .filter(spot => (!props.showClosedSpots ? !yardStore.isSpotClosed(spot) : true))
          .filter(spot => {
            if (props.assetType === 'assetVisit') {
              // when it is a assetVisit, the spot can have a container only in it
              // or it can be empty
              return (
                (yardStore.hasAssetContainerOnSpot(spot) && !yardStore.hasAssetVisitOnSpot(spot)) ||
                yardStore.isSpotOpen(spot)
              );
            }

            if (props.assetType === 'assetContainer') {
              // when it is a assetContainer, the spot MUST NOT have a container in it
              // or it can be empty
              return !yardStore.hasAssetContainerOnSpot(spot) || yardStore.isSpotOpen(spot);
            }

            return true;
          })
          .map(spot => ({
            code: spot.code,
            id: spot.id,
            icon: yardStore.getSpotIcon(spot.type),
            group: area.name
          }))
          .sort((a, b) => StrLib.compareAlphaNumeric(a.code, b.code));

        items.push({ header: area.name.toUpperCase() });
        items.push(...areaSpots);
      }

      return items;
    });

    return {
      value,
      selectedAssetVisit: yardStore.selectedAssetVisitRef,
      selectedSpot: yardStore.selectedSpotRef,
      spotAreas: yardStore.spotAreasRef,
      spots: yardStore.spotsRef,
      spotsByArea,
      searchTerm,
      t
    };
  }
});
</script>
